import { type FC, useId } from "react";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { CloseButton } from "mds/components/CloseButton";
import { Tooltip } from "mds/components/Tooltip";
import {
  VersionHistoryScrubber,
  type VersionHistoryScrubberMark,
} from "worksheets/views/timeline/VersionHistoryScrubber";

type VersionHistoryCardProps = {
  snapshotIndex: number;
  snapshotCount: number;
  className?: string;
  dateLabel: string;
  eventLabel: string;
  eventLabelTooltip?: string;
  marks: VersionHistoryScrubberMark[];
  onChange: (index: number) => void;
  onClose: () => void;
};

export const VersionHistoryCard: FC<VersionHistoryCardProps> = ({
  snapshotIndex,
  snapshotCount,
  className,
  dateLabel,
  eventLabel,
  eventLabelTooltip,
  marks,
  onChange,
  onClose,
}) => {
  const headingId = useId();
  const descriptionId = useId();
  const valueText = eventLabel
    ? t("worksheet.timeline.slider_value", { dateLabel, eventLabel })
    : dateLabel;

  return (
    <div className={twMerge("sticky top-0 z-20 flex w-full flex-col bg-white", className)}>
      <Card
        className="page-block-width mx-auto flex w-full flex-col gap-4 rounded-2xl px-6 py-4"
        kind="blue"
      >
        <div className="flex w-full flex-col gap-[15px]">
          <div className="flex w-full flex-col gap-1">
            <div className="flex w-full items-end justify-between">
              <h3 className="h3 text-blue" id={headingId}>
                {t("worksheet.timeline.title")}
              </h3>

              <CloseButton onClick={onClose} />
            </div>

            <p className="body-xs text-black-tint-40" id={descriptionId}>
              {t("worksheet.timeline.description")}
            </p>
          </div>

          <div className="body-s flex w-full min-w-0 flex-col gap-1 text-black-tint-20">
            <p>{dateLabel}</p>

            {eventLabelTooltip ? (
              <Tooltip content={eventLabelTooltip} kind="card" wrapperClassName="min-w-0 w-full">
                {(tooltipAnchorProps) => (
                  <button
                    className="min-h-[1lh] w-full truncate bg-transparent p-0 text-left outline-none"
                    type="button"
                    {...tooltipAnchorProps}
                  >
                    {eventLabel}
                  </button>
                )}
              </Tooltip>
            ) : (
              <p className="min-h-[1lh] truncate">{eventLabel || "\u00a0"}</p>
            )}
          </div>
        </div>

        <VersionHistoryScrubber
          count={snapshotCount}
          describedBy={descriptionId}
          labelledBy={headingId}
          marks={marks}
          value={snapshotIndex}
          valueText={valueText}
          onChange={onChange}
        />
      </Card>
    </div>
  );
};
